import React, { useEffect, useRef, useState } from 'react';
import { Box, Text } from 'ink';
import type { SettingsInputState, SettingsItem, SettingsSelectorItem } from '../types';
import { useKeyHandler } from '../lib/key_handler';
import { useTheme, type ThemeTokens } from '../lib/theme';

export const settingsFooterHint = (): string | null =>
  '↑/↓ to navigate • Enter/Space to change • Esc to close';

export interface SettingsCallbacks {
  onToggle: (key: string) => void;
  /** Commit a value for a non-boolean selector item. */
  onSelect?: (key: string, value: string) => void;
  /**
   * A selector option is highlighted but not committed yet. Handlers can use
   * this to live-preview the value; it also fires with the committed value
   * when the option list is dismissed, to undo any preview.
   */
  onHighlight?: (key: string, value: string) => void;
  onClose: () => void;
}

/** The label shown on the right of an item: the selected option, or on/off for toggles. */
const itemStatus = (tokens: ThemeTokens, item: SettingsItem): { text: string; color: string } => {
  if (item.options) {
    const selected = item.options.find((option) => option.value === item.value);
    return { text: selected?.label ?? item.value, color: tokens.text.accent };
  }
  return item.enabled
    ? { text: 'on', color: tokens.text.success }
    : { text: 'off', color: tokens.text.danger };
};

interface SettingsInputProps {
  input: SettingsInputState;
  callbacks: SettingsCallbacks;
}

export const SettingsInput: React.FC<SettingsInputProps> = ({ input, callbacks }) => {
  const { tokens } = useTheme();
  const [selectedIndex, setSelectedIndex] = useState(input.selectedIndex);
  // When set, the selected selector item shows its option list and arrow keys
  // move through options (previewing them) instead of through settings items.
  const [expandedItem, setExpandedItem] = useState<SettingsSelectorItem | null>(null);
  const [optionIndex, setOptionIndex] = useState(0);

  // If the input is torn down externally (e.g. a session event replaces the input
  // type) while an option list is open, restore the committed value so an
  // un-committed preview does not linger. Refs keep this unmount-only: a cleanup
  // keyed on `expandedItem` would also fire right after Enter and re-apply the
  // stale pre-commit value.
  const expandedRef = useRef(expandedItem);
  expandedRef.current = expandedItem;
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;
  useEffect(
    () => () => {
      const item = expandedRef.current;
      if (item) callbacksRef.current.onHighlight?.(item.key, item.value);
    },
    [],
  );

  useKeyHandler((event) => {
    if (event.eventType !== 'press') return;

    if (expandedItem) {
      if (event.name === 'escape' || (event.ctrl && event.name === 'c')) {
        // Dismiss without committing: restore the committed value over any preview.
        callbacks.onHighlight?.(expandedItem.key, expandedItem.value);
        setExpandedItem(null);
        event.stopPropagation();
        return;
      }

      if (event.name === 'return' || event.name === 'space') {
        callbacks.onSelect?.(expandedItem.key, expandedItem.options[optionIndex].value);
        setExpandedItem(null);
        event.stopPropagation();
        return;
      }

      if (event.name === 'up' && optionIndex > 0) {
        const next = optionIndex - 1;
        setOptionIndex(next);
        callbacks.onHighlight?.(expandedItem.key, expandedItem.options[next].value);
        event.stopPropagation();
        return;
      }

      if (event.name === 'down' && optionIndex < expandedItem.options.length - 1) {
        const next = optionIndex + 1;
        setOptionIndex(next);
        callbacks.onHighlight?.(expandedItem.key, expandedItem.options[next].value);
        event.stopPropagation();
      }
      return;
    }

    if (event.name === 'escape' || (event.ctrl && event.name === 'c')) {
      callbacks.onClose();
      event.stopPropagation();
      return;
    }

    if (event.name === 'return' || event.name === 'space') {
      const item = input.items[selectedIndex];
      if (item?.disabled) {
        event.stopPropagation();
        return;
      }
      if (item?.options) {
        const currentIndex = item.options.findIndex((option) => option.value === item.value);
        setOptionIndex(Math.max(currentIndex, 0));
        setExpandedItem(item);
      } else if (item) {
        callbacks.onToggle(item.key);
      }
      event.stopPropagation();
      return;
    }

    if (event.name === 'up' && selectedIndex > 0) {
      setSelectedIndex(selectedIndex - 1);
      event.stopPropagation();
      return;
    }

    if (event.name === 'down' && selectedIndex < input.items.length - 1) {
      setSelectedIndex(selectedIndex + 1);
      event.stopPropagation();
    }
  });

  const selectedItem = input.items[selectedIndex];

  return (
    <Box flexDirection="column">
      <Box
        borderStyle="round"
        borderColor={tokens.text.accent}
        paddingX={2}
        paddingY={1}
        flexDirection="column"
      >
        <Box marginBottom={1}>
          <Text bold color={tokens.text.accent}>
            Settings
          </Text>
        </Box>

        <Box flexDirection="column" marginBottom={1}>
          {input.items.map((item, index) => {
            const isSelected = index === selectedIndex;
            const expandedOptions = expandedItem?.key === item.key ? item.options : undefined;
            // While the option list is open, the status reflects the highlighted
            // (previewed) option rather than the committed value.
            const status = expandedOptions
              ? { text: expandedOptions[optionIndex].label, color: tokens.text.accent }
              : itemStatus(tokens, item);
            return (
              <Box key={item.key} flexDirection="column">
                <Box gap={1}>
                  <Text color={isSelected ? tokens.text.default : tokens.text.subtle}>
                    {isSelected ? '→' : ' '}
                  </Text>
                  <Box width={34}>
                    <Text bold={isSelected} dimColor={item.disabled}>
                      {item.label}
                    </Text>
                  </Box>
                  <Text color={status.color} dimColor={item.disabled}>
                    {status.text}
                  </Text>
                </Box>
                {expandedOptions?.map((option, i) => {
                  const isHighlighted = i === optionIndex;
                  return (
                    <Box key={option.value} gap={1} marginLeft={4}>
                      <Text color={isHighlighted ? tokens.text.default : tokens.text.subtle}>
                        {isHighlighted ? '→' : ' '}
                      </Text>
                      <Text
                        bold={isHighlighted}
                        color={option.value === item.value ? tokens.text.accent : undefined}
                      >
                        {option.label}
                      </Text>
                    </Box>
                  );
                })}
              </Box>
            );
          })}
        </Box>

        <Box>
          <Text dimColor>
            {expandedItem
              ? '↑/↓ to preview • Enter to select • Esc to cancel'
              : selectedItem?.description}
          </Text>
        </Box>
      </Box>
    </Box>
  );
};
